import { Checkbox, FieldError, FieldHelp, FieldLabel, Input } from '@speaking-coach/ui'
/**
 * The editable client fields, shared by the create form and the client
 * detail page so the two cannot drift apart.
 *
 * Every field here maps 1:1 to a column the operator route accepts.
 * Nothing commercial appears: no term, renewal, payment state, invoice
 * or metering (ADR-0052 D3b), and no access window — start and end
 * dates belong to the cohort, because a client runs several engagements
 * with different windows (D3c).
 */

export function NameField({
  value,
  onChange,
  error = null,
}: {
  value: string
  onChange: (v: string) => void
  /** Server or schema message for this field. */
  error?: string | null
}): JSX.Element {
  return (
    <div>
      <FieldLabel
        htmlFor="client-name"
        label="Client name"
        help="How the client appears throughout the admin and on certificates."
      />
      <Input
        id="client-name"
        type="text"
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <FieldError message={error} />
    </div>
  )
}

export function SlugField({
  value,
  onChange,
  locked = false,
  error = null,
}: {
  value: string
  onChange: (v: string) => void
  /** True once a student has activated — the server rejects a change. */
  locked?: boolean
  /** Server or schema message for this field. */
  error?: string | null
}): JSX.Element {
  return (
    <div>
      <FieldLabel
        htmlFor="client-slug"
        label="Slug"
        help={
          <>
            Lowercase letters, digits and single hyphens. This is an operator
            handle and appears in no student-facing URL. It locks once the first
            student activates. A Japanese-only name gets a stable generated
            handle; replace it with preferred romaji if useful.
          </>
        }
      />
      <Input
        id="client-slug"
        type="text"
        required
        value={value}
        readOnly={locked}
        onChange={(e) => onChange(e.target.value)}
        surface={locked ? 'raised' : 'inset'}
        className={locked ? 'font-mono text-text-secondary' : 'font-mono'}
      />
      <FieldError message={error} />
      {locked && (
        <p className="text-text-secondary text-caption mt-1">
          Locked after student activation.
        </p>
      )}
    </div>
  )
}

export function TrophyTargetField({
  value,
  onChange,
  error = null,
}: {
  value: number
  onChange: (v: number) => void
  /** Server or schema message for this field. */
  error?: string | null
}): JSX.Element {
  const errorId = 'client-trophy-target-error'
  return (
    <div>
      <FieldLabel
        htmlFor="client-trophy-target"
        label="Trophy target"
        help={
          <>
            Default target for new participation. Sales normally picks 150,
            200 or 270; the final goal is locked on each enrollment.
          </>
        }
      />
      <Input
        id="client-trophy-target"
        type="number"
        min={1}
        required
        value={value}
        aria-invalid={error !== null}
        aria-describedby={error !== null ? errorId : undefined}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <FieldError id={errorId} message={error} />
    </div>
  )
}

export function InternalField({
  value,
  onChange,
}: {
  value: boolean
  onChange: (v: boolean) => void
}): JSX.Element {
  return (
    <div className="flex items-start gap-3 py-2">
      <Checkbox
        id="client-internal"
        checked={value}
        onCheckedChange={(checked) => onChange(checked === true)}
        className="mt-1"
      />
      <div className="flex items-center gap-1 min-h-6">
        <label htmlFor="client-internal" className="text-body font-medium">
          Internal (not a customer)
        </label>
        <FieldHelp label="internal client classification">
          Excludes this client from customer analytics and KPI rollups. It does
          not change product access or email behavior. The default applies to
          students created from now on; existing students keep their original
          classification.
        </FieldHelp>
      </div>
    </div>
  )
}

export function CompanyInformationSetting({ field, label, value, onChange }: {
  field: string; label: string; value: boolean; onChange: (value: boolean) => void
}): JSX.Element {
  return <div className="flex items-center gap-3 py-2">
    <Checkbox id={field} checked={value} onCheckedChange={(checked) => onChange(checked === true)} />
    <FieldLabel htmlFor={field} label={label} help="Enable optional company information in student provisioning and HR reports. Turning this off hides the field from HR and preserves saved values." />
  </div>
}
